import React, { useEffect, useState } from "react";
import { Menu, X, Calendar } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";
import { openCalendly } from "../lib/calendly";

export const Header = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "team", label: t.nav.team },
    { id: "presence", label: t.nav.presence },
    { id: "news", label: t.nav.news },
    { id: "contact", label: t.nav.contact },
  ];

  const handleNav = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#0D2F4C]/10 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between h-20">
        <Logo variant={scrolled ? "dark" : "dark"} />

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-btn`}
              onClick={() => handleNav(l.id)}
              className="text-sm tracking-wide text-[#0D2F4C] hover:opacity-70 transition-opacity duration-200 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#0D2F4C] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher variant="dark" />
          <button
            data-testid="header-book-btn"
            onClick={() => openCalendly()}
            className="bg-[#C9A961] text-[#0D2F4C] px-4 py-2.5 text-sm tracking-wide hover:bg-[#D4B775] transition-colors duration-200 flex items-center gap-2 font-medium"
            style={{ borderRadius: 0 }}
            aria-label={t.nav.book_call}
          >
            <Calendar size={14} strokeWidth={2.2} />
            <span>{t.nav.book_call}</span>
          </button>
          <button
            data-testid="header-cta-btn"
            onClick={() => handleNav("contact")}
            className="bg-[#0D2F4C] text-white px-5 py-2.5 text-sm tracking-wide hover:bg-[#0A243A] transition-colors duration-200"
            style={{ borderRadius: 0 }}
          >
            {t.nav.cta}
          </button>
        </div>

        <button
          data-testid="mobile-menu-btn"
          className="lg:hidden text-[#0D2F4C] p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-[#0D2F4C]/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}-btn`}
                onClick={() => handleNav(l.id)}
                className="text-left text-[#0D2F4C] text-base"
              >
                {l.label}
              </button>
            ))}
            <div className="pt-4 border-t border-[#0D2F4C]/10 flex flex-col gap-3">
              <button
                data-testid="mobile-book-btn"
                onClick={() => {
                  setMobileOpen(false);
                  openCalendly();
                }}
                className="bg-[#C9A961] text-[#0D2F4C] px-5 py-3 text-sm flex items-center justify-center gap-2 font-medium"
                style={{ borderRadius: 0 }}
              >
                <Calendar size={14} strokeWidth={2.2} />
                <span>{t.nav.book_call}</span>
              </button>
              <div className="flex items-center justify-between">
                <LanguageSwitcher variant="dark" />
                <button
                  data-testid="mobile-cta-btn"
                  onClick={() => handleNav("contact")}
                  className="bg-[#0D2F4C] text-white px-5 py-2.5 text-sm"
                  style={{ borderRadius: 0 }}
                >
                  {t.nav.cta}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
